import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  clearAuthError,
  clearAuthSuccess,
  selectAuthError,
  selectAuthSuccess,
  selectUpdateEmailLoading,
  selectUpdateNicknameLoading,
  selectUpdatePasswordLoading,
  updateUserEmail,
  updateUserNickname,
  updateUserPassword,
} from "../userSlice";

const ProfileSettings = ({ user, session }) => {
  const dispatch = useDispatch();
  const updateEmailLoading = useSelector(selectUpdateEmailLoading);
  const updatePasswordLoading = useSelector(selectUpdatePasswordLoading);
  const updateNicknameLoading = useSelector(selectUpdateNicknameLoading);
  const error = useSelector(selectAuthError);
  const success = useSelector(selectAuthSuccess);

  // State for editing mode and form data
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nickname: user.nickname || "",
    email: session?.user?.email || "",
    password: "",
    confirmPassword: "",
  });

  // Update form data when user/session changes
  useEffect(() => {
    setFormData({
      nickname: user.nickname || "",
      email: session?.user?.email || "",
      password: "",
      confirmPassword: "",
    });
  }, [user.nickname, session?.user?.email]);

  // Clear errors when entering/exiting edit mode
  useEffect(() => {
    if (isEditing) {
      dispatch(clearAuthError());
      dispatch(clearAuthSuccess());
    }
  }, [isEditing, dispatch]);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    dispatch(clearAuthError());
    dispatch(clearAuthSuccess());
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setFormData({
      nickname: user.nickname || "",
      email: session?.user?.email || "",
      password: "",
      confirmPassword: "",
    });
    dispatch(clearAuthError());
    dispatch(clearAuthSuccess());
  };

  const handleUpdateNickname = async (e) => {
    e.preventDefault();
    if (formData.nickname.trim() && formData.nickname !== user.nickname) {
      await dispatch(
        updateUserNickname({ nickname: formData.nickname.trim() })
      );
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (formData.email.trim() && formData.email !== session?.user?.email) {
      await dispatch(updateUserEmail({ email: formData.email.trim() }));
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (formData.password && formData.password === formData.confirmPassword) {
      await dispatch(updateUserPassword({ password: formData.password }));
      setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }));
    }
  };

  const isAnyLoading =
    updateEmailLoading || updatePasswordLoading || updateNicknameLoading;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "2px solid",
        borderColor: "primary.main",
        borderRadius: 3,
        p: 3,
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Profile Settings
        </Typography>
        {!isEditing ? (
          <Button variant="contained" onClick={handleStartEditing} size="small">
            Edit Profile
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={handleCancelEditing}
            disabled={isAnyLoading}
            size="small"
          >
            Cancel
          </Button>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          onClose={() => dispatch(clearAuthError())}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          onClose={() => dispatch(clearAuthSuccess())}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      {!isEditing ? (
        // Display mode
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Nickname
            </Typography>
            <Typography variant="body1">
              {user.nickname || "Not set"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1">
              {session?.user?.email || "Not available"}
            </Typography>
          </Box>
        </Stack>
      ) : (
        // Edit mode
        <Stack spacing={3}>
          {/* Nickname Update */}
          <Box component="form" onSubmit={handleUpdateNickname}>
            <Stack spacing={2}>
              <TextField
                label="Nickname"
                name="nickname"
                value={formData.nickname}
                onChange={handleInputChange}
                disabled={isAnyLoading}
                fullWidth
                placeholder="How should we call you?"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={
                  updateNicknameLoading ||
                  !formData.nickname.trim() ||
                  formData.nickname === user.nickname
                }
                size="small"
                sx={{ alignSelf: "flex-start" }}
              >
                {updateNicknameLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Update Nickname"
                )}
              </Button>
            </Stack>
          </Box>

          {/* Email Update */}
          <Box component="form" onSubmit={handleUpdateEmail}>
            <Stack spacing={2}>
              <TextField
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isAnyLoading}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                disabled={
                  updateEmailLoading ||
                  !formData.email.trim() ||
                  formData.email === session?.user?.email
                }
                size="small"
                sx={{ alignSelf: "flex-start" }}
              >
                {updateEmailLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Update Email"
                )}
              </Button>
            </Stack>
          </Box>

          {/* Password Update */}
          <Box component="form" onSubmit={handleUpdatePassword}>
            <Stack spacing={2}>
              <TextField
                label="New Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                disabled={isAnyLoading}
                fullWidth
                helperText="Password must be at least 6 characters"
              />
              <TextField
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={isAnyLoading}
                fullWidth
                error={
                  formData.confirmPassword &&
                  formData.password !== formData.confirmPassword
                }
                helperText={
                  formData.confirmPassword &&
                  formData.password !== formData.confirmPassword
                    ? "Passwords do not match"
                    : ""
                }
              />
              <Button
                type="submit"
                variant="contained"
                disabled={
                  updatePasswordLoading ||
                  !formData.password ||
                  formData.password !== formData.confirmPassword ||
                  formData.password.length < 6
                }
                size="small"
                sx={{ alignSelf: "flex-start" }}
              >
                {updatePasswordLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Update Password"
                )}
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default ProfileSettings;
